import * as React from "react";
import { render, unmount } from "rc-util/lib/React/render";
import { RiskFactor, VerifyStatus, isIPhone } from "@/common/js/constant";
import { i18nTranslate } from "@didi/i18n-plugin";
import { Modal, Popup } from "antd-mobile";
import { highlightMarkerSafe } from "@/utils/highlight";
import { setCommonAttrs, trackEvent } from "@/utils/omega";
import { inApp, IsReachLimitStatus, BioVerifyStatus, LimitSource } from "@/common/js/constant";
import SwitchFactor from "./SwitchFactor";
import App from "@/utils/app";
import { LoadingButton } from "@/components/LoadingButton";
import warningErrorIcon from "../assets/images/warning-error.svg";
import type { RiskVerifyUiVariant } from "../types";
// import {IsReachLimitStatus}

// 页面参数
type PageAnswerProps = {
  type: RiskFactor;
  uiVariant?: RiskVerifyUiVariant;
  onSubmit: (
    value: string,
    currentVerifyType: RiskFactor,
    triggerId: string,
  ) => Promise<RiskVerifyResult>;
  onTrigger: (
    currentVerifyType: RiskFactor,
  ) => Promise<{ verifyInfo: MethodDetail } & SwitchVerifyDetail>;
  onSuccess: (token: RiskVerifyResult) => void;
  onCancel?: (cancelByUser: boolean | undefined) => void;
  onSwitch?: (verifyType: RiskFactor) => void;
};

// 埋点上报页面/事件映射
const MapBuryPointEvent: {
  [name: string]: { page: string; click: string; show: string; result: string; limit: string };
} = {
  // 手机短信
  [RiskFactor.SMS_VERIFY]: {
    page: "verification_captcha",
    // 20240829 @王思懿 事件名加上sofi
    click: "fin_sofi_verification_captcha_ck",
    show: "fin_sofi_verification_captcha_sw",
    result: "fin_sofi_verification_captcha_fail_st",
    // 20241025 @王思懿 新增限次埋点
    limit: "fin_sofi_verification_block_sw",
  },
  // 邮箱验证码
  [RiskFactor.EMAIL_VERIFY]: {
    page: "verification_email",
    click: "fin_sofi_verification_email_ck",
    show: "fin_sofi_verification_email_sw",
    result: "fin_sofi_verification_email_fail_st",
    // 20241025 @王思懿 新增限次埋点
    limit: "fin_sofi_verification_block_sw",
  },
  // curp
  [RiskFactor.CURP_VERIFY]: {
    page: "sofi_risk_control_curp",
    click: "fin_sofi_risk_control_curp_ck",
    show: "fin_sofi_risk_control_curp_sw",
    result: "fin_sofi_risk_control_curp_st",
    // 20241025 @王思懿 新增限次埋点
    limit: "fin_sofi_verification_block_sw",
  },
  // bio埋点 20250904生物核验无点击限次等节点埋点
  [RiskFactor.BIO_VERIFY]: {
    page: "sofi_risk_control_bio",
    click: "fin_sofi_verification_bio_ck",
    show: "fin_sofi_verification_bio_sw",
    result: "fin_sofi_verification_bio_st",
    limit: "fin_sofi_verification_block_sw",
  },
  // WhatsApp 渠道触达
  [RiskFactor.WHATSAPP_VERIFY]: {
    page: "verification_captcha",
    // 20240829 @王思懿 事件名加上sofi
    click: "fin_sofi_verification_captcha_ck",
    show: "fin_sofi_verification_captcha_sw",
    result: "fin_sofi_verification_captcha_fail_st",
    // 20241025 @王思懿 新增限次埋点
    limit: "fin_sofi_verification_block_sw",
  },
  // 业务核验 - 短信
  [RiskFactor.SMS]: {
    page: "待补充",
    click: "待补充",
    show: "待补充",
    result: "待补充",
    limit: "待补充",
  },
};

// console.log(MapBuryPointEvent[RiskFactor.SMS_VERIFY].click)

export default function PageAnswer(props: PageAnswerProps) {
  const isUno = props.uiVariant === "uno";
  const hasVisualViewport = typeof window !== "undefined" && !!window.visualViewport;
  //
  const [verifyDetail, setVerifyDetail] = React.useState<
    { verifyInfo: MethodDetail } & SwitchVerifyDetail
  >({
    verifyInfo: { title: "", subTitle: "", triggerId: "", secretKey: "" },
    supportSwitchVerify: false,
  });
  // 错误提示
  const [errorTips, setErrorTips] = React.useState<string>("");
  // 答案
  const [answer, setAnswer] = React.useState<string>("");
  // 核验码长度; cpf核验为4位输入框 其他为6位
  const [length] = React.useState<number>(props.type === RiskFactor.CURP_VERIFY ? 4 : 6);
  // 输入前line为灰色，输入后为蓝色
  const lineStyle = answer ? "line-style" : "normal-line-style";
  // 输入框样式
  const inputClass = length === 4 ? "box-style" : lineStyle;
  // 倒计时
  const [times, setTimes] = React.useState<number>(0);
  // 是否聚焦
  const [isFocus, setIsFocus] = React.useState<boolean>(false);
  // 是否提交
  const [isSubmit, setIsSubmit] = React.useState<boolean>(false);
  const [popupFragment] = React.useState<DocumentFragment>(document.createDocumentFragment());
  // 核验方式是否是生物核验
  const [isBio] = React.useState<boolean>(props.type === RiskFactor.BIO_VERIFY ? true : false);
  const needTriggerId = props.type !== RiskFactor.BIO_VERIFY;
  const triggerId = verifyDetail.verifyInfo.triggerId;
  const pageAnswerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  // 用户取消
  const handleCancel = (cancelByUser?: boolean) => {
    if (cancelByUser) {
      // 上报埋点
      trackEvent(MapBuryPointEvent[props.type].click, {
        button: "return",
        // 核验类型
        otp_source: props.type,
      });
    }
    props.onCancel && props.onCancel(cancelByUser);
    // 删除source
    setCommonAttrs({ source: "" });
  };
  // 用户输入内容
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = async (evt) => {
    // 提交状态也静默输入
    if (isSubmit) {
      return;
    }

    const value: string = evt.target.value;
    // trigger 未完成时不提交满位验证码，避免 check 早于 trigger 发出。
    if (
      value.length === length &&
      props.type !== RiskFactor.CURP_VERIFY &&
      needTriggerId &&
      !triggerId
    ) {
      return;
    }
    // 更新输入
    setAnswer(evt.target.value.substring(0, length));
    // 如果验证码长度满足自动提交
    if (value.length === length && props.type !== RiskFactor.CURP_VERIFY) {
      handleSubmit(evt.target.value.substring(0, length));
      return;
    } else {
      setErrorTips("");
    }
  };
  // 关闭切换弹窗
  const handleCloseSwitchPopup = () => {
    unmount(popupFragment);
  };
  // 切换核验方式
  const handleSwitchFactor = (verifyType: RiskFactor) => {
    props.onSwitch && props.onSwitch(verifyType);
    // 关闭弹窗
    handleCloseSwitchPopup();
  };
  // 生物核验提交
  const handleBioVerifySubmit = async (
    bioDetail: BioDetail,
    verifyDetail: {
      verifyInfo: MethodDetail;
    } & SwitchVerifyDetail,
  ) => {
    // 生物核验提交专属埋点
    trackEvent(MapBuryPointEvent[props.type].click, {
      button: "confirm",
      result_status: "0",
      // 核验类型
      otp_source: props.type,
    });
    if (isSubmit) {
      return;
    }
    // 开始提交
    setIsSubmit(true);
    try {
      const token = await props.onSubmit(
        bioDetail?.biologyVerifyResult || "",
        props.type,
        verifyDetail.verifyInfo.triggerId,
      );
      props.onSuccess(token);
      //生物核验成功埋点，0909数据确认成功不需要上报限制来源
      trackEvent("fin_sofi_verification_biology_page_fail_st", {
        success: BioVerifyStatus.SUCCESS,
        is_reach_limit: IsReachLimitStatus.NO,
        retry_count: bioDetail?.retryCount,
        // 核验类型
        otp_source: props.type,
      });
    } catch (e: any) {
      setAnswer("");
      //生物核验成功和失败埋点
      trackEvent("fin_sofi_verification_biology_page_fail_st", {
        success: 0,
        is_reach_limit:
          e.status === VerifyStatus.LIMIT ? IsReachLimitStatus.YES : bioDetail?.systemLimit,
        limit_source: e.status === VerifyStatus.LIMIT ? LimitSource.BACKEND : LimitSource.SYSTEM,
        retry_count: bioDetail?.retryCount,
        // 核验类型
        otp_source: props.type,
      });

      // 触发重试逻辑
      if (e.status === VerifyStatus.LIMIT || e.status === VerifyStatus.FAIL) {
        // 是否自动发起重试，status = fail/limit并且 autoRetryTrigger = true && retryTriggerDetail 不为空，按照retryTriggerDetail发起直接发起trigger重试
        if (e.autoRetryTrigger === true && !!e.retryTriggerDetail) {
          handleSwitchFactor(e.retryTriggerDetail?.name);
        } else {
          // 让 input 失去焦点
          inputRef?.current?.blur();
          // 弹窗提示
          setTimeout(() => {
            Modal.alert({
              className: "sofi-alert",
              content: e?.errorInfo?.title || i18nTranslate("Fintech_Payment_AF_fOga_XwyT"),
              confirmText: e?.errorInfo?.button?.title || "Ok",
              onConfirm() {
                // 终止核验流程
                handleCancel();
              },
            });
          }, 100);
          return;
        }
      } else {
        handleCancel();
      }
    }
    // 提交完成
    setIsSubmit(false);
  };

  // 触发核验
  const handleTrigger = (button: string) => {
    // 倒计时结束前静默操作
    if (times > 0 && button === "resend") {
      return;
    }
    // 提交状态也静默trigger
    if (isSubmit) {
      return;
    }

    if (verifyDetail.supportSwitchVerify) {
      const answerPopupHeight = pageAnswerRef.current?.getBoundingClientRect().height;
      setCommonAttrs({ source: props.type });
      // 上报埋点
      trackEvent(MapBuryPointEvent[props.type].click, {
        button,
        result_status: "0",
        // 核验类型
        otp_source: props.type,
      });
      // 渲染核验弹窗
      render(
        <Popup
          visible
          mask={isUno}
          position="bottom"
          bodyClassName="sdk-risk-verify sdk-risk-verify-popup sdk-risk-verify-switch-popup"
          bodyStyle={{ backgroundColor: "transparent" }}
          maskStyle={isUno ? { backgroundColor: "rgba(12, 12, 13, 0.8)" } : undefined}
          style={{ "--z-index": "9999" }}
        >
          <SwitchFactor
            detail={verifyDetail}
            uiVariant={props.uiVariant}
            minHeight={answerPopupHeight}
            onSelect={handleSwitchFactor}
            onCancel={handleCloseSwitchPopup}
          />
        </Popup>,
        popupFragment,
      );
      return;
    }

    // 开始提交
    setIsSubmit(true);

    // 上报埋点
    trackEvent(MapBuryPointEvent[props.type].click, {
      button,
      result_status: "0",
      // 核验类型
      otp_source: props.type,
    });

    // 重置提示文案
    setErrorTips("");

    // 增加限次逻辑
    props
      .onTrigger(props.type)
      .then((verifyDetail) => {
        setVerifyDetail(verifyDetail);
        //核验方式是BIO就拉起生物核验
        if (isBio) {
          App.getBioVerifyResult(verifyDetail?.verifyInfo?.secretKey || "")
            .then((res) => {
              if (res && Object.keys(res).length > 0) {
                // 成功拉起生物核验展示faceid或指纹
                // 上报埋点 生物核验展示
                trackEvent("fin_sofi_verification_biology_page_sw", {
                  // 核验类型
                  otp_source: props.type,
                });
              }
              if (res?.isInterrupted) {
                // 用户主动取消核验，终止核验流程
                handleCancel();
              } else {
                handleBioVerifySubmit(res, verifyDetail);
              }
            })
            .catch((err) => {
              trackEvent("tech_sofi_catch_error", {
                source: "riskverify.onTrigger.getBioVerifyResult",
                data: JSON.stringify(err),
              });
              handleBioVerifySubmit(err, verifyDetail);
            });
        }
      })
      .catch((e) => {
        // 限次逻辑
        if (e.status === VerifyStatus.LIMIT) {
          // 上报埋点 限次
          trackEvent(MapBuryPointEvent[props.type].limit, {
            popup_type: e?.errorInfo?.title || i18nTranslate("Fintech_Payment_AF_fOga_XwyT"),
            // 核验类型
            otp_source: props.type,
          });

          // 让 input 失去焦点
          inputRef?.current?.blur();

          setTimeout(() => {
            Modal.alert({
              className: "sofi-alert",
              // title: e.errorInfo?.title || i18nTranslate('Fintech_Payment_AF_fOga_XwyT'),
              content: e.errorInfo?.title || i18nTranslate("Fintech_Payment_AF_fOga_XwyT"),
              confirmText: e.errorInfo?.button?.title || "Ok",
              onConfirm() {
                // 触发限次后终止核验流程
                handleCancel();
              },
            });
          }, 100);
          return;
        }

        // 显示错误提示
        setErrorTips(e.errorInfo?.title || i18nTranslate("Fintech_Payment_AF_fOga_XwyT"));
      })
      .finally(() => {
        // 提交完成
        setIsSubmit(false);
      });
    // 更新计时器
    setTimes(60);
  };
  // 提交答案
  const handleSubmit = async (answer: string) => {
    // 上报埋点
    trackEvent(MapBuryPointEvent[props.type].click, {
      button: "confirm",
      result_status: "0",
      // 核验类型
      otp_source: props.type,
    });
    // 提交过程不允许再
    if (isSubmit) {
      return;
    }
    if (needTriggerId && !triggerId) {
      return;
    }
    // 开始提交
    setIsSubmit(true);

    try {
      const token = await props.onSubmit(answer, props.type, triggerId);
      props.onSuccess(token);
      // 上报埋点 成功
      trackEvent(MapBuryPointEvent[props.type].result, {
        // success 是否验证成功
        // 1=成功，0=失败
        success: 1,
        // 是否有多次尝试toast展现
        // toast_sw
        // 0 未展示
        // 1 展示
        toast_sw: 0,
        // toast 文案
        toast_msg: "",
        // 核验类型
        otp_source: props.type,
      });
    } catch (e: any) {
      // 验证码验证失败 清空 @吴比
      // http://eng.xiaojukeji.com/biz/718/requirement/bug/669857
      if (
        props.type === RiskFactor.EMAIL_VERIFY ||
        props.type === RiskFactor.SMS_VERIFY ||
        // 20251205新增whatsapp核验失败后清空
        props.type === RiskFactor.WHATSAPP_VERIFY
      ) {
        setAnswer("");
      }

      // 上报埋点 失败
      trackEvent(MapBuryPointEvent[props.type].result, {
        // success 是否验证成功
        // 1=成功，0=失败
        success: 0,
        // 是否有多次尝试toast展现
        // toast_sw
        // 0 未展示
        // 1 展示
        toast_sw: 1,
        // toast 文案
        toast_msg: e?.errorInfo?.title || i18nTranslate("Fintech_Payment_AF_fOga_XwyT"),
        // 核验类型
        otp_source: props.type,
      });

      // 触发限次逻辑
      if (e.status === VerifyStatus.LIMIT) {
        // 上报埋点 限次
        trackEvent(MapBuryPointEvent[props.type].limit, {
          popup_type: e?.errorInfo?.title || i18nTranslate("Fintech_Payment_AF_fOga_XwyT"),
          // 核验类型
          otp_source: props.type,
        });

        // 让 input 失去焦点
        inputRef?.current?.blur();
        // 弹窗提示
        setTimeout(() => {
          Modal.alert({
            className: "sofi-alert",
            // title: e.errorInfo?.title || i18nTranslate('Fintech_Payment_AF_fOga_XwyT'),
            content: e?.errorInfo?.title || i18nTranslate("Fintech_Payment_AF_fOga_XwyT"),
            confirmText: e?.errorInfo?.button?.title || "Ok",
            onConfirm() {
              // 触发限次后终止核验流程
              handleCancel();
            },
          });
        }, 100);
        return;
      }

      // 上报埋点 失败
      trackEvent(MapBuryPointEvent[props.type].result, {
        // success 是否验证成功
        // 1=成功，0=失败
        success: 0,
        // 是否有多次尝试toast展现
        // toast_sw
        // 0 未展示
        // 1 展示
        toast_sw: 0,
        // toast 文案
        toast_msg: "",
        // 核验类型
        otp_source: props.type,
      });

      // 显示错误提示
      setErrorTips(e.errorInfo?.title || i18nTranslate("Fintech_Payment_AF_fOga_XwyT"));
    }

    // 提交完成
    setIsSubmit(false);
  };
  // input聚焦时候 将弹窗滚动到视口
  const handleFocus = () => {
    setIsFocus(true);
  };
  // input失焦时候 重置位置
  const handleBlur = () => {
    setTimeout(() => setIsFocus(false));
  };

  // UNO 弹窗在键盘弹出时跟随 visual viewport，避免只移动内部面板造成遮罩错位。
  React.useEffect(() => {
    if (!isUno || !isFocus) {
      return;
    }

    const popupBody = pageAnswerRef.current?.closest(
      ".sdk-risk-verify-answer-popup",
    ) as HTMLElement | null;
    if (!popupBody) {
      return;
    }

    const viewport = window.visualViewport;
    const updateKeyboardOffset = () => {
      const keyboardOffset = viewport
        ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
        : 0;
      popupBody.style.bottom = `${keyboardOffset}px`;
    };

    updateKeyboardOffset();
    viewport?.addEventListener("resize", updateKeyboardOffset);
    viewport?.addEventListener("scroll", updateKeyboardOffset);
    window.addEventListener("resize", updateKeyboardOffset);

    return () => {
      viewport?.removeEventListener("resize", updateKeyboardOffset);
      viewport?.removeEventListener("scroll", updateKeyboardOffset);
      window.removeEventListener("resize", updateKeyboardOffset);
      popupBody.style.removeProperty("bottom");
    };
  }, [isUno, isFocus]);

  // 倒计时
  React.useEffect(() => {
    setTimeout(() => {
      if (times > 0) {
        setTimes(times - 1);
      }
    }, 1000 * 1);
  }, [times]);

  // 高亮内容
  const highlightHtml: string = highlightMarkerSafe(verifyDetail.verifyInfo.subTitle || "");

  //
  React.useEffect(handleTrigger.bind(null, "resend"), [props.type]);
  // did mount
  React.useEffect(() => {
    // 设置pub page
    setCommonAttrs({ pub_page: MapBuryPointEvent[props.type].page });
    // 上报埋点
    trackEvent(MapBuryPointEvent[props.type].show, {
      // 核验类型
      otp_source: props.type,
    });
  }, [props.type]);

  return (
    <div
      ref={pageAnswerRef}
      className={`page-answer ${isUno ? "page-answer-uno" : ""} ${isFocus ? "answer-focus" : ""} ${isIPhone ? "safari" : ""} ${inApp && isIPhone && isFocus ? "answer-focus-inapp" : ""} ${isUno && isFocus && hasVisualViewport ? "answer-focus-viewport" : ""} ${isBio ? "bio-display" : ""}`}
    >
      <h1>{verifyDetail.verifyInfo.title}</h1>
      <p dangerouslySetInnerHTML={{ __html: highlightHtml }}></p>
      {props.type === RiskFactor.WHATSAPP_VERIFY ? (
        <div className="whatsapp-icon" onClick={handleTrigger.bind(null, "have_no_whatsapp")}>
          {i18nTranslate("Fintech_Payment_OTP_pEfV_rJQv")}
        </div>
      ) : null}
      <div className="form-answer">
        <div className={`code-input ${inputClass} ${errorTips ? "verify-error" : ""}`}>
          {new Array(length).fill("").map((item, index) => (
            <span key={index} className={`code-item ${answer[index] ? "active" : "normal"}`}>
              {answer[index] || (isUno ? "" : 0)}
            </span>
          ))}
        </div>
        {/* 输入框：非 CURP 使用数字键盘（tel + inputMode='numeric'）*/}
        <input
          ref={inputRef}
          type={props.type !== RiskFactor.CURP_VERIFY ? "tel" : "text"}
          inputMode={props.type !== RiskFactor.CURP_VERIFY ? "numeric" : undefined}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          value={answer}
        />
        {/* 错误提示：UNO 预留错误行，避免错误文案与底部按钮重叠 */}
        {isUno ? (
          <div
            className={`verify-error-tips verify-error-tips-uno ${errorTips ? "" : "is-empty"}`}
            role={errorTips ? "alert" : undefined}
          >
            <img className="verify-error-icon" src={warningErrorIcon} alt="" />
            <span>{errorTips}</span>
          </div>
        ) : (
          <div className="verify-error-tips">{errorTips}</div>
        )}
      </div>
      <div className="btn-group">
        {props.type === RiskFactor.CURP_VERIFY ? (
          isUno ? (
            <LoadingButton
              className="risk-verify-uno-button"
              disabled={answer.length < 4 || isSubmit}
              onClick={handleSubmit.bind(null, answer)}
            >
              {i18nTranslate("Fintech_Payment_AF_gZgm_wjTw")}
            </LoadingButton>
          ) : (
            <div
              className={`btn-confirm ${answer.length < 4 ? "normal" : "active"}`}
              onClick={handleSubmit.bind(null, answer)}
            >
              {i18nTranslate("Fintech_Payment_AF_gZgm_wjTw")}
            </div>
          )
        ) : isUno ? (
          <LoadingButton
            className="risk-verify-uno-button"
            disabled={Boolean(times || isSubmit)}
            onClick={handleTrigger.bind(null, "resend")}
          >
            {i18nTranslate("Fintech_Payment_KYC_xGBA_EheK")}
            {times ? ` (${times}s)` : null}
          </LoadingButton>
        ) : (
          <div
            className={`btn-confirm ${times || isSubmit ? "normal" : "active"}`}
            onClick={handleTrigger.bind(null, "resend")}
          >
            {i18nTranslate("Fintech_Payment_KYC_xGBA_EheK")}
            {times ? ` (${times}s)` : null}
          </div>
        )}
      </div>
      {isUno ? (
        <button
          className="btn-close btn-close-uno"
          type="button"
          aria-label="Close"
          onClick={handleCancel.bind(null, true)}
        >
          <img
            src="https://s3-us01.didiglobal.com/silver-bullet-img/2026-08-15/1Rg4Lj/__.png"
            alt=""
          />
        </button>
      ) : (
        <span className="btn-close" onClick={handleCancel.bind(null, true)}></span>
      )}
    </div>
  );
}
